import { useKeyboardNavigation } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/NavigationFooter/hook/useKeyboardNavigation';
import { isDeclarationDisabled } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/Review/component/KycActionButton/utils/isDeclarationDisabled';
import { isKycDisabled } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/Review/component/KycActionButton/utils/isKycDisabled';
import {
	getApprovalStatus,
	getButtonStatus,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/Review/component/KycActionButton/utils/utils';
import { getKycButtonProps } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/Review/utils/getKycButtonProps';
import { StyledButtonContainer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/styled';
import {
	Step,
	StepType,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/types';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	AcceptanceStatus,
	DataKYCFileTctWithFile,
	GetKYCInvestorInstanceRes,
} from '@dealstack/shared/lib/generated';
import { Box, Flex, Text, Tooltip, useMantineTheme } from '@mantine/core';
import { IIconProps } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.types';
import type { ButtonVariant } from './KycActionButton.types';

interface Props {
	onClick: () => Promise<void>;
	variant: ButtonVariant;
	isLoading?: boolean;
	isDisabled?: boolean;
	currentStep: Step | null;
	selectedKycFile: DataKYCFileTctWithFile | undefined;
	currentInvestor: GetKYCInvestorInstanceRes | undefined;
	dataTestId?: string;
	approvalStatusOverride?: AcceptanceStatus;
}

export const KycActionButton = ({
	onClick,
	currentStep,
	variant,
	selectedKycFile,
	currentInvestor,
	isLoading = false,
	isDisabled: isDisabledProp,
	dataTestId,
	approvalStatusOverride,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const theme = useMantineTheme();
	const buttonProps = getKycButtonProps(theme, tMipManage);
	const isDeclarationsStep = currentStep?.type === StepType.DECLARATIONS;
	const approvalStatus =
		getApprovalStatus(
			isDeclarationsStep,
			currentInvestor,
			approvalStatusOverride ?? selectedKycFile?.status ?? undefined
		) ?? undefined;
	const status = getButtonStatus(approvalStatus, variant);

	const resolveIsDisabled = () => {
		if (isDeclarationsStep) {
			return isDeclarationDisabled({ currentStep, currentInvestor, variant });
		}
		if (approvalStatusOverride !== undefined) {
			return false;
		}
		return isKycDisabled({ selectedKycFile, variant });
	};

	const isDisabled = isDisabledProp || resolveIsDisabled();

	useKeyboardNavigation([
		{
			key: 'Enter',
			action: () => {
				if (variant === 'approve') {
					void onClick();
				}
			},
		},
		{
			key: 'Space',
			action: () => {
				if (variant === 'decline' && !isDisabled) {
					void onClick();
				}
			},
		},
	]);

	return (
		<Tooltip label={buttonProps[status]?.toolTipText} position="bottom">
			<StyledButtonContainer
				data-testid={dataTestId}
				onClick={onClick}
				color={buttonProps[status]?.backgroundColor}
				$onHoverBackgroundColor={buttonProps[status]?.onHoverBackgroundColor}
				$onHoverTextColor={buttonProps[status]?.onHoverTextColor}
				$onHoverIconColor={buttonProps[status]?.onHoverIconColor}
				$iconColor={buttonProps[status]?.iconColor}
				$loading={isLoading}
				disabled={isDisabled ?? isLoading}
				className={status}
			>
				<Flex direction="column" justify="center" align="center" gap="0.25rem">
					{isLoading ? (
						<Box data-testid="loading-icon">
							<Icon variant="thinking-loading" size="1.5rem" />
						</Box>
					) : (
						<>
							<Box data-testid="icon-one">
								<Icon
									variant={buttonProps[status]?.variant as IIconProps['variant']}
									size="1.5rem"
									className="un-hovered-state"
								/>
							</Box>
							<Box data-testid="icon-two">
								<Icon
									variant={
										buttonProps[status]?.onHoverVariant as IIconProps['variant']
									}
									size="1.5rem"
									className="hovered-state"
								/>
							</Box>
						</>
					)}

					<Text c={buttonProps[status]?.textColor} className="un-hovered-state">
						{buttonProps[status]?.text}
					</Text>

					<Text c={buttonProps[status]?.textColor} className="hovered-state">
						{buttonProps[status]?.onHoverText}
					</Text>
				</Flex>
			</StyledButtonContainer>
		</Tooltip>
	);
};
